/**
 * Reusable rail button leaf component for {@link SidebarRail}.
 *
 * In manager ("Module settings") mode it renders as a Framer `Reorder.Item` so
 * the categories can be dragged into a new order — smoothly and on touch — via a
 * 3-dot grip on the left.
 */

import type { RefObject } from 'react';
import { Reorder, useDragControls } from 'framer-motion';

/** Pure helper — extracted so it can be unit-tested without React rendering. */
export function computeShowSubItems(opts: {
  showExpander: boolean;
  isGroupOpen?: boolean;
  expanded: boolean;
  alwaysOpen?: boolean;
}): boolean {
  return (opts.showExpander && (opts.isGroupOpen ?? false)) || (opts.expanded && (opts.alwaysOpen ?? false));
}

export function RailItem({
  railKey,
  label,
  activeKey,
  expanded,
  onClick,
  onHover,
  badge,
  count,
  isGroupOpen,
  subItems,
  onSubItemClick,
  forceChevron,
  alwaysOpen,
  dimmed,
  toggle,
  reorderValue,
  draggable,
  shake,
  onReorderEnd,
  dragConstraints,
  children,
}: {
  railKey: string;
  label: string;
  activeKey: string | null;
  expanded: boolean;
  onClick: (key: string) => void;
  /** Fired on pointer-enter / focus — used to warm the module's chunk + data
   *  before a click. Purely additive; callers that omit it are unaffected. */
  onHover?: (key: string) => void;
  badge?: boolean;
  count?: number;
  isGroupOpen?: boolean;
  subItems?: Array<{ id: string; label: string; count?: number }>;
  onSubItemClick?: (moduleId: string) => void;
  /** Show chevron toggle even when count < 2 (e.g. channels inline list). */
  forceChevron?: boolean;
  /** Always show sub-items in expanded rail without showing a chevron toggle. */
  alwaysOpen?: boolean;
  dimmed?: boolean;
  toggle?: { on: boolean; onToggle: () => void };
  /** When set, render as a Framer Reorder.Item with this value (the rail key) so
   *  the item lives in the shared Reorder layout and animates smoothly when the
   *  list re-sorts (e.g. a module toggled on/off slides to its group boundary). */
  reorderValue?: string;
  /** Off / plugin rows stay in the Reorder.Group (so they animate) but must not
   *  start a drag — `draggable={false}` hides the grip and the drag never arms. */
  draggable?: boolean;
  shake?: boolean;
  /** Fired when a reorder drag ends, so the parent can persist the new order. */
  onReorderEnd?: () => void;
  /** Container ref the drag is clamped to, so the item can't leave the list. */
  dragConstraints?: RefObject<HTMLElement | null>;
  children: React.ReactNode;
}) {
  const reorderMode = reorderValue !== undefined;
  const canDrag = reorderMode && draggable !== false;
  const dragControls = useDragControls();
  const showBadge = expanded && count !== undefined && count >= 2 && !toggle;
  const showChevron = showBadge || (expanded && (forceChevron ?? false) && !toggle);
  const showExpander = showChevron;
  const showSubItems = computeShowSubItems({ showExpander, isGroupOpen, expanded, alwaysOpen });

  const groupClassName = `rail-item-group${dimmed ? ' dimmed' : ''}${reorderMode ? ' reorderable' : ''}${shake ? ' shake' : ''}`;
  const showToggle = !!toggle && expanded;

  const inner = (
    <>
      {/* `.rail-row` is a positioned wrapper — NOT itself a button — so the
          manager-toggle can live as a sibling of `.rail-btn` (never nested
          inside it: nested <button>s produce React's "cannot be a descendant
          of" invalid-nesting error, see #5624/#5542) while still being
          absolutely positioned over the row's right edge for visual parity
          with the old nested layout.
          `.has-toggle` reserves headroom for the absolutely-positioned toggle:
          pulling the toggle out of flow means `.rail-btn` no longer widens to
          make room for it, so an unbounded label (plugin rows fall back to
          tenant-provided names — Shopify/Mixpanel project names, no length
          cap) could otherwise render underneath the toggle pill. */}
      <div className={`rail-row${showToggle ? ' has-toggle' : ''}`}>
        <button
          className={`rail-btn${activeKey === railKey && !dimmed ? ' active' : ''}`}
          onClick={() => onClick(railKey)}
          // Warm the module chunk + data on hover/focus intent. Suppressed in
          // reorder mode so dragging a row doesn't spam prefetch. The handler is
          // idempotent, so this gate is for cleanliness, not correctness.
          onMouseEnter={!reorderMode ? () => onHover?.(railKey) : undefined}
          onFocus={!reorderMode ? () => onHover?.(railKey) : undefined}
        >
          {canDrag && expanded && (
            // 3-dot grip on the far left — drag starts from here (the rest of the
            // row still navigates / toggles). Drag is pointer-based so it works on
            // touch too.
            <span
              className="rail-reorder-handle"
              aria-label="Drag to reorder"
              onPointerDown={(e) => { e.stopPropagation(); dragControls.start(e); }}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="rail-reorder-dot" />
              <span className="rail-reorder-dot" />
              <span className="rail-reorder-dot" />
            </span>
          )}
          <span className="rail-btn-icon">{children}</span>
          {badge && !dimmed && <span className="rail-badge" />}
          <span className="rail-btn-label">{label}</span>
          {showBadge && <span className="rail-count-badge">{count}</span>}
          {showExpander && (
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              className={`rail-chevron${isGroupOpen ? ' open' : ''}`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          )}
          {!expanded && <span className="rail-tooltip">{label}</span>}
        </button>
        {showToggle && toggle && (
          <button
            className={`manager-toggle${toggle.on ? ' on' : ''}`}
            aria-label={`Toggle ${label}`}
            onClick={(e) => { e.stopPropagation(); toggle.onToggle(); }}
          />
        )}
      </div>
      {showSubItems && subItems && (
        <div className="rail-sub-items">
          {subItems.map((item) => (
            <button
              key={item.id}
              className={`rail-sub-item${item.count ? ' has-unread' : ''}`}
              onClick={() => onSubItemClick?.(item.id)}
            >
              <span className="rail-sub-item-label">{item.label}</span>
              {item.count != null && item.count > 0 && (
                <span className="rail-sub-item-count">{item.count > 9 ? '9+' : item.count}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </>
  );

  if (reorderMode) {
    // Every manage-mode row (on, off, plugin) is a Reorder.Item so it shares the
    // group's layout context and animates when the list re-sorts. Only `canDrag`
    // rows arm the pointer drag (off/plugin rows render no grip and stay put).
    return (
      <Reorder.Item
        value={reorderValue}
        as="div"
        className={groupClassName}
        drag={canDrag ? 'y' : false}
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={dragConstraints}
        dragElastic={0.04}
        onDragEnd={() => onReorderEnd?.()}
      >
        {inner}
      </Reorder.Item>
    );
  }

  return (
    <div className={groupClassName}>
      {inner}
    </div>
  );
}
